/* ============================================================
   PLP infinite scroll + browsing-position restoration

   Appends Shopify's own server-rendered product cards. No product
   card HTML is ever built in JavaScript — every appended <li> is a
   node parsed straight out of a Shopify section response, so images,
   swatches, badges, quick add, Notify Me and pricing arrive exactly
   as Liquid rendered them.

   Integration notes:

   - facets.js replaces #ProductGridContainer.innerHTML on every
     filter/sort change. This element lives inside that container, so
     it is destroyed and recreated on each change: disconnectedCallback
     aborts in-flight work, connectedCallback re-reads the new
     pagination. That is the whole of the filter/sort reset — facets.js
     is untouched.

   - Newly appended cards need no rebinding. Quick add (<modal-opener>,
     <product-form>, <quick-add-bulk>) are custom elements, so the
     browser upgrades them on append, and notify-me.js is delegated on
     document. The only exception is the reveal-on-scroll animation,
     which is handed to the theme's own initializeScrollAnimationTrigger.

   - Browser history is never touched during normal scrolling: no
     pushState, no page= entries, no URL rewriting. Back still goes
     PDP -> PLP in one step.
   ============================================================ */

(function () {
  'use strict';

  var STORAGE_PREFIX = 'plp-infinite:';

  /* How early to start loading. Measured against this store, a page of 36
     cards costs 660-1020ms, effectively all of it Shopify's server response
     (~500KB of card markup; client-side parsing is ~9ms). Nothing here can
     make that round trip shorter — the only lever is starting it sooner, so
     this is deliberately further out than a typical 700px trigger. Combined
     with one-page-ahead prefetching below, the append itself becomes instant
     for every page after the first. */
  var ROOT_MARGIN = '1400px 0px 1400px 0px';

  /* Ceiling on a Back-button restore, so a customer who scrolled very
     deep cannot trigger an unbounded fan-out of requests. */
  var MAX_RESTORE_PAGES = 10;

  /* Two consecutive failures and we stop for good, revealing the real
     pagination again rather than retrying forever. */
  var MAX_FAILURES = 2;

  /* ---------- page cache (Back-button restoration only) ----------

     Measured on this store, rebuilding a 7-page PLP on Back costs ~1090ms of
     network for ~3.2MB; parsing the same pages already in hand costs ~38ms.

     Deliberately the Cache API rather than sessionStorage: 3.2MB is at or over
     the typical ~5MB sessionStorage quota, and sessionStorage is synchronous,
     so writing that much would block the main thread on every page load.

     The cache is WRITTEN while browsing forward and READ only when restoring.
     Forward scrolling therefore always shows live Shopify output; only the
     Back-button rebuild is allowed to be up to CACHE_TTL stale, and
     revalidateAvailability() corrects it moments later. */
  var CACHE_NAME = 'plp-infinite-pages-v1';
  var CACHE_TTL = 120000; // 2 minutes
  var CACHE_STAMP_HEADER = 'x-plp-cached-at';

  function cacheAvailable() {
    return typeof caches !== 'undefined' && window.isSecureContext !== false;
  }

  async function cachePut(url, text) {
    if (!cacheAvailable()) return;
    try {
      var cache = await caches.open(CACHE_NAME);
      var headers = { 'content-type': 'text/html' };
      headers[CACHE_STAMP_HEADER] = String(Date.now());
      await cache.put(url, new Response(text, { headers: headers }));
    } catch (e) {
      /* Quota or private mode — restoration just falls back to the network. */
    }
  }

  async function cacheGet(url) {
    if (!cacheAvailable()) return null;
    try {
      var cache = await caches.open(CACHE_NAME);
      var hit = await cache.match(url);
      if (!hit) return null;

      var stamp = parseInt(hit.headers.get(CACHE_STAMP_HEADER), 10);
      if (!stamp || Date.now() - stamp > CACHE_TTL) {
        cache.delete(url);
        return null;
      }
      return await hit.text();
    } catch (e) {
      return null;
    }
  }

  /* Entries outlive the tab, so expired ones are swept on init. */
  async function cachePrune() {
    if (!cacheAvailable()) return;
    try {
      var cache = await caches.open(CACHE_NAME);
      var keys = await cache.keys();
      await Promise.all(
        keys.map(async function (request) {
          var hit = await cache.match(request);
          var stamp = hit ? parseInt(hit.headers.get(CACHE_STAMP_HEADER), 10) : 0;
          if (!stamp || Date.now() - stamp > CACHE_TTL) await cache.delete(request);
        })
      );
    } catch (e) {
      /* no-op */
    }
  }

  /* What makes a restored card stale: whether it can be bought. Compared
     between the cached card and the freshly fetched one so revalidation can
     replace only the cards whose availability actually moved. */
  function availabilitySignature(item) {
    if (!item) return '';
    return [
      item.querySelector('.product-badge--sold-out') ? 'soldout' : '',
      item.querySelector('.notify-me') ? 'notify' : '',
      item.querySelector('.quick-add') ? 'quickadd' : '',
    ].join('|');
  }

  function productIdOf(item) {
    var link = item.querySelector('[id^="CardLink-"]');
    if (!link) return null;
    var parts = link.id.split('-');
    return parts[parts.length - 1];
  }

  /* Restoration must run at most once per document load. A filter change
     recreates the element, and that must never re-trigger a restore. */
  var restoreAttempted = false;

  /* Upper bound on the wait for layout before the saved scroll position is
     applied. Two animation frames is the correct signal, but rAF is
     throttled in a backgrounded or occluded tab, and the restoration dim
     must never outlive it — so the frames race a short timeout. */
  var LAYOUT_TIMEOUT = 250;

  function waitForLayout() {
    return Promise.race([
      new Promise(function (resolve) {
        requestAnimationFrame(function () {
          requestAnimationFrame(resolve);
        });
      }),
      new Promise(function (resolve) {
        setTimeout(resolve, LAYOUT_TIMEOUT);
      }),
    ]);
  }

  /* One canonical form for every pagination URL, so a URL taken from
     Shopify's paginate object, a URL parsed out of a section response
     (which carries section_id) and a URL read back from sessionStorage
     all compare equal. Everything stored or compared goes through this. */
  function normalizeUrl(url) {
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.delete('section_id');
      return parsed.toString();
    } catch (e) {
      return url;
    }
  }

  function navigationType() {
    try {
      var entries = performance.getEntriesByType('navigation');
      if (entries && entries.length) return entries[0].type;
      // Deprecated fallback for older Safari.
      if (performance.navigation) {
        return performance.navigation.type === 2 ? 'back_forward' : 'navigate';
      }
    } catch (e) {
      /* no-op */
    }
    return 'navigate';
  }

  /* Storage key identifies one PLP *state*, not one PLP. Filters and sort
     live in the query string, so including the sorted params means a
     different filter or sort combination can never read back another
     one's products or scroll position. `page` is excluded so landing on
     ?page=3 shares a key with page 1. */
  function stateKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    var pairs = [];
    params.forEach(function (value, key) {
      pairs.push(key + '=' + value);
    });
    pairs.sort();
    return STORAGE_PREFIX + window.location.pathname + '?' + pairs.join('&');
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(stateKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(stateKey(), JSON.stringify(state));
    } catch (e) {
      /* Private mode or quota — infinite scroll still works, only
         restoration is lost. Never break the PLP over this. */
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(stateKey());
    } catch (e) {
      /* no-op */
    }
  }

  class PlpInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.gridId = this.dataset.gridId || 'product-grid';
      this.grid = document.getElementById(this.gridId);
      this.sentinel = this.querySelector('[data-sentinel]');
      this.statusEl = this.querySelector('[data-status]');
      this.skeletonTemplate = this.querySelector('[data-skeleton-template]');

      if (!this.grid || !this.sentinel || !this.skeletonTemplate) return;

      this.sectionId = this.dataset.sectionId || '';
      this.nextUrl = this.dataset.nextUrl ? normalizeUrl(this.dataset.nextUrl) : null;
      this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;
      this.loadedPages = [];
      this.loading = false;
      this.finished = false;
      this.failures = 0;
      this.lastProductId = null;
      this.controller = null;

      /* Defensive: if a grid swap happened while skeletons were on screen and
         the replacement markup was serialized from the live DOM rather than a
         fresh server response, stale placeholders can survive into the new
         grid. Nothing else would ever remove them, so clear them at init. */
      this.removeSkeletons();

      this.onProductClick = this.onProductClick.bind(this);
      this.onPageHide = this.onPageHide.bind(this);
      this.grid.addEventListener('click', this.onProductClick, true);
      window.addEventListener('pagehide', this.onPageHide);

      this.prefetch = null;
      this.revalidateController = null;

      /* Cache entries outlive the tab; sweep expired ones without blocking. */
      cachePrune();

      /* Hide the pagination immediately, but only once the preconditions for
         infinite scroll have actually been checked (see canInitialise) — so
         there is no flash of page numbers on load, and a broken setup still
         degrades to working pagination rather than stranding the customer. */
      if (!this.canInitialise()) return;
      this.hidePagination(true);

      if (restoreAttempted) {
        this.observe();
        return;
      }
      restoreAttempted = true;

      var saved = navigationType() === 'back_forward' ? readState() : null;

      if (!saved) {
        /* A deliberate fresh visit (menu click, reload) must not inherit
           an old scroll position. Drop the stale state outright. */
        clearState();
        this.observe();
        return;
      }

      this.restore(saved);
    }

    disconnectedCallback() {
      /* Fired when facets.js swaps the grid for a new filter/sort result,
         which cancels any request whose products belong to the old state. */
      if (this.controller) this.controller.abort();
      if (this.revalidateController) this.revalidateController.abort();
      /* A warm page belongs to the old filter/sort state — drop it. */
      this.clearPrefetch(true);
      if (this.observer) this.observer.disconnect();
      /* A cancelled request must not leave its placeholders behind. */
      this.removeSkeletons();
      if (this.grid) this.grid.removeEventListener('click', this.onProductClick, true);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    /* Everything infinite scroll depends on, checked synchronously before the
       pagination is taken away. The offsetHeight check is not paranoia: Dawn's
       base.css carries `div:empty { display: none }`, which silently removed
       this sentinel's layout box and made the observer permanently inert. If
       any precondition regresses, the customer keeps real pagination. */
    canInitialise() {
      if (!('IntersectionObserver' in window) || typeof AbortController === 'undefined') return false;
      if (!this.nextUrl) return false;
      return this.sentinel.offsetHeight > 0 || this.sentinel.offsetWidth > 0;
    }

    /* ---------- observation ---------- */

    observe() {
      if (this.finished || !this.nextUrl) {
        this.finish();
        return;
      }
      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: ROOT_MARGIN }
      );
      this.observer.observe(this.sentinel);
    }

    finish() {
      this.finished = true;
      if (this.observer) this.observer.disconnect();
      this.clearPrefetch(true);
      this.removeSkeletons();
      this.setStatus('');
    }

    /* Stop and hand the customer back to real pagination. */
    bailOut() {
      this.finish();
      this.hidePagination(false);
    }

    /* ---------- fetching ---------- */

    /* Requests the section only, so a page costs the grid markup rather
       than a whole document, and carries every existing param (filters,
       sort, search) because the URL comes from Shopify's paginate object. */
    /* Parses a page out of HTML already in hand. Shared by the network path
       and the cache path so both produce identical results. */
    parsePage(text) {
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var grid = doc.getElementById(this.gridId);
      var component = doc.querySelector('plp-infinite-scroll');

      return {
        items: grid ? Array.from(grid.querySelectorAll(':scope > li.grid__item')) : [],
        nextUrl: component && component.dataset.nextUrl ? normalizeUrl(component.dataset.nextUrl) : null,
      };
    }

    /* Restoration only: the cached copy of a page, or null. */
    async fetchPageFromCache(url) {
      var canonical = normalizeUrl(url);
      var text = await cacheGet(canonical);
      if (!text) return null;

      var parsed = this.parsePage(text);
      if (!parsed.items.length) return null;

      parsed.url = canonical;
      parsed.fromCache = true;
      return parsed;
    }

    async fetchPage(url, signal) {
      var canonical = normalizeUrl(url);

      var sectionUrl = new URL(canonical);
      if (this.sectionId) sectionUrl.searchParams.set('section_id', this.sectionId);

      var parsed = await this.requestAndParse(sectionUrl.toString(), signal, canonical);

      /* If a section render did not yield a grid for any reason, fall back
         to the full page URL rather than losing the page. */
      if (!parsed.items.length) {
        parsed = await this.requestAndParse(canonical, signal);
      }

      parsed.url = canonical;
      parsed.nextUrl = parsed.nextUrl ? normalizeUrl(parsed.nextUrl) : null;
      return parsed;
    }

    async requestAndParse(url, signal, cacheKey) {
      var response = await fetch(url, { signal: signal });
      if (!response.ok) throw new Error('Pagination request failed: ' + response.status);

      var text = await response.text();
      var parsed = this.parsePage(text);

      /* Cached for a possible Back-button return. Fire and forget: a cache
         write must never delay showing products. */
      if (cacheKey && parsed.items.length) cachePut(cacheKey, text);

      return parsed;
    }

    /* ---------- prefetching ---------- */

    /* Warms exactly one page ahead, started only after the customer has
       already pulled a page in — so a visitor who never scrolls costs no extra
       request, and a visitor who keeps scrolling never waits on the server.
       The ~800ms round trip happens while they are still reading the row above. */
    prefetchNext() {
      if (this.finished || !this.nextUrl) return;

      var url = normalizeUrl(this.nextUrl);
      if (this.prefetch && this.prefetch.url === url) return;
      if (this.loadedPages.indexOf(url) !== -1) return;

      var controller = new AbortController();
      var record = { url: url, controller: controller, settled: false, error: null, page: null };

      /* Resolves rather than rejects: a failed warm-up is not a customer-facing
         failure, it just means loadNext falls back to fetching normally. */
      record.promise = this.fetchPage(url, controller.signal).then(
        function (page) {
          record.settled = true;
          record.page = page;
          return page;
        },
        function (error) {
          record.settled = true;
          record.error = error;
          return null;
        }
      );

      this.prefetch = record;
    }

    clearPrefetch(abort) {
      if (this.prefetch && abort) this.prefetch.controller.abort();
      this.prefetch = null;
    }

    /* ---------- normal scrolling ---------- */

    async loadNext() {
      if (this.loading || this.finished || !this.nextUrl) return;

      var requestUrl = normalizeUrl(this.nextUrl);
      if (this.loadedPages.indexOf(requestUrl) !== -1) return; // never re-fetch

      /* A warm page for this exact URL, unless it failed — in which case drop
         it and fetch normally. */
      var warm = this.prefetch && this.prefetch.url === requestUrl ? this.prefetch : null;
      if (warm && warm.error) {
        this.clearPrefetch(false);
        warm = null;
      }

      this.loading = true;
      this.controller = new AbortController();

      /* Skip the placeholders entirely when the page is already in hand —
         showing and removing skeletons within one frame is just a flicker. */
      if (!warm || !warm.settled) {
        this.showSkeletons();
        this.setStatus(this.dataset.loadingText || '');
      }

      try {
        var page;
        if (warm) {
          page = await warm.promise;
          this.clearPrefetch(false);
        }
        if (!page) {
          page = await this.fetchPage(requestUrl, this.controller.signal);
        }

        /* The element may have been replaced by a filter change while the
           request was in flight — those products belong to a dead state. */
        if (!this.isConnected) return;

        this.appendItems(page.items);
        this.loadedPages.push(page.url);
        this.nextUrl = page.nextUrl;
        this.failures = 0;
        this.saveState();

        /* Start warming the following page straight away, so continuous
           scrolling never waits on the server again. */
        this.prefetchNext();

        if (!this.nextUrl) {
          this.finish();
        } else {
          /* Re-arm rather than wait for the next intersection *change*.
             IntersectionObserver only reports transitions, so if the sentinel
             is still inside the 700px margin once this page has been appended
             — a tall viewport, a short page, or a customer sitting at the
             bottom — no further callback would ever fire and loading would
             stall until they happened to scroll again. Creating a fresh
             observer re-evaluates the current position immediately. */
          this.observe();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.removeSkeletons();
        this.failures += 1;
        if (this.failures >= MAX_FAILURES) this.bailOut();
      } finally {
        this.loading = false;
        this.controller = null;
        if (this.isConnected && !this.finished) {
          this.removeSkeletons();
          this.setStatus('');
        }
      }
    }

    /* ---------- Back-button restoration ---------- */

    /* Every previously loaded page URL was saved, so all of them can be
       fetched in parallel and committed in ONE DOM write. That is what
       stops the customer watching page 1, then 2, then 3 rebuild. */
    async restore(saved) {
      var urls = Array.isArray(saved.pages) ? saved.pages.slice(0, MAX_RESTORE_PAGES) : [];

      if (!urls.length) {
        /* Only page 1 had been loaded — no content to rebuild, just put the
           customer back where they were once layout has settled. */
        this.nextUrl = saved.nextUrl ? normalizeUrl(saved.nextUrl) : this.nextUrl;
        requestAnimationFrame(() => {
          this.applySavedScroll(saved);
          this.observe();
        });
        return;
      }

      /* The browser would otherwise restore scroll against a page-1-sized
         document and land the customer in the wrong place. */
      try {
        history.scrollRestoration = 'manual';
      } catch (e) {
        /* no-op */
      }

      this.setRestoring(true);
      this.showSkeletons();
      this.controller = new AbortController();
      var signal = this.controller.signal;

      try {
        /* Cache first, network only for the misses. A warm cache turns a
           ~1090ms network rebuild into ~38ms of parsing. */
        var pages = await Promise.all(
          urls.map(async (url) => {
            var cached = await this.fetchPageFromCache(url);
            return cached || this.fetchPage(url, signal);
          })
        );
        if (!this.isConnected) return;

        var servedFromCache = urls.filter(function (url, index) {
          return pages[index] && pages[index].fromCache;
        });

        var fragment = document.createDocumentFragment();
        pages.forEach(function (page) {
          page.items.forEach(function (item) {
            fragment.appendChild(item);
          });
        });

        this.removeSkeletons();
        this.setStatus('');
        this.grid.appendChild(fragment);

        this.loadedPages = pages.map(function (page) {
          return page.url;
        });
        this.nextUrl = pages[pages.length - 1].nextUrl;

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(this.grid);
        }

        /* Two frames: the first lets the appended rows land, the second
           guarantees layout has been recalculated before we read
           scrollHeight and set the final position. */
        await waitForLayout();

        this.applySavedScroll(saved);

        /* Anything shown from cache may be up to CACHE_TTL out of date on
           availability. Re-check it against Shopify now that the customer is
           already looking at their products. */
        if (servedFromCache.length) this.revalidateAvailability(servedFromCache);
      } catch (error) {
        /* Restoration is a nicety — never leave the customer staring at
           skeletons. Fall back to the normal page-1 PLP. */
        if (error.name !== 'AbortError') this.nextUrl = this.dataset.nextUrl || null;
      } finally {
        this.controller = null;
        this.removeSkeletons();
        this.setStatus('');
        this.setRestoring(false);
        try {
          history.scrollRestoration = 'auto';
        } catch (e) {
          /* no-op */
        }
        if (this.isConnected) {
          this.observe();
          /* Warm the page after the restored ones, so resumed scrolling is
             instant just like normal scrolling. */
          this.prefetchNext();
        }
      }
    }

    /* ---------- background availability refresh ---------- */

    /* Re-fetches the pages that were served from cache and replaces ONLY the
       cards whose buyability changed — sold out, back in stock. Sequential and
       unhurried: correctness of what is on screen matters, speed does not,
       because the customer already has their products.

       Whole <li> elements are swapped rather than patched, so a refreshed card
       is exactly what Liquid would have rendered. Replacing like with like at
       identical dimensions causes no layout shift, and a card the customer is
       actually touching is skipped rather than yanked away. */
    async revalidateAvailability(urls) {
      this.revalidateController = new AbortController();
      var signal = this.revalidateController.signal;

      for (var i = 0; i < urls.length; i += 1) {
        if (!this.isConnected || signal.aborted) return;
        try {
          var fresh = await this.fetchPage(urls[i], signal);
          if (!this.isConnected || signal.aborted) return;
          this.syncAvailability(fresh.items);
        } catch (error) {
          return; // stale data is survivable; hammering the server is not
        }
      }
    }

    syncAvailability(freshItems) {
      freshItems.forEach((freshItem) => {
        var productId = productIdOf(freshItem);
        if (!productId) return;

        var current = this.findCard(productId);
        if (!current) return;
        if (availabilitySignature(current) === availabilitySignature(freshItem)) return;

        /* Never replace a card the customer is interacting with. */
        if (current.contains(document.activeElement)) return;
        if (current.querySelector('.quick-add-modal[open], dialog[open], details[open]')) return;

        current.replaceWith(freshItem);
      });
    }

    /* Priority: saved pixel position, then the clicked product as anchor. */
    applySavedScroll(saved) {
      var maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      var targetY = typeof saved.scrollY === 'number' ? saved.scrollY : 0;

      if (targetY <= maxY) {
        window.scrollTo(0, targetY);
        return;
      }

      var anchor = saved.productId ? this.findCard(saved.productId) : null;
      if (anchor) {
        anchor.scrollIntoView({ block: 'center' });
        return;
      }

      window.scrollTo(0, maxY);
    }

    findCard(productId) {
      /* Reuses the CardLink id already present on every card. A suffix match
         is unambiguous because the separator is part of the pattern:
         "-123" cannot match "CardLink-x-4123". */
      var selector = '[id^="CardLink-"][id$="-' + productId + '"]';
      var link = this.grid.querySelector(selector);
      return link ? link.closest('li.grid__item') : null;
    }

    /* ---------- DOM helpers ---------- */

    appendItems(items) {
      if (!items.length) return;
      var fragment = document.createDocumentFragment();
      items.forEach(function (item) {
        fragment.appendChild(item);
      });

      this.removeSkeletons();
      this.grid.appendChild(fragment);

      /* The theme's own reveal-on-scroll initialiser, so appended cards
         behave identically to cards rendered on first paint. */
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this.grid);
      }
    }

    /* Roughly one row, matched to the grid's current column count, so the
       placeholder reads as "more is coming" rather than a whole page. */
    skeletonCount() {
      var desktop = parseInt(this.dataset.columnsDesktop, 10) || 4;
      var mobile = parseInt(this.dataset.columnsMobile, 10) || 2;
      return window.matchMedia('(min-width: 990px)').matches ? desktop : mobile;
    }

    showSkeletons() {
      if (!this.grid || this.grid.querySelector('.plp-skeleton')) return; // already showing

      var count = this.skeletonCount();
      var fragment = document.createDocumentFragment();
      for (var i = 0; i < count; i += 1) {
        fragment.appendChild(this.skeletonTemplate.content.cloneNode(true));
      }
      this.grid.appendChild(fragment);
    }

    removeSkeletons() {
      if (!this.grid) return;
      this.grid.querySelectorAll('.plp-skeleton').forEach(function (node) {
        node.remove();
      });
    }

    setRestoring(active) {
      var wrapper = this.closest('.collection') || this.parentElement;
      if (wrapper) wrapper.classList.toggle('plp-restoring', !!active);
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text;
    }

    hidePagination(hidden) {
      var scope = this.closest('#ProductGridContainer') || document;
      var wrapper = scope.querySelector('.pagination-wrapper');
      if (wrapper) wrapper.classList.toggle('plp-pagination-hidden', !!hidden);
    }

    /* ---------- state ---------- */

    onProductClick(event) {
      var link = event.target.closest('a[href]');
      if (!link || !this.grid.contains(link)) return;

      var item = link.closest('li.grid__item');
      var cardLink = item ? item.querySelector('[id^="CardLink-"]') : null;
      if (cardLink) {
        var parts = cardLink.id.split('-');
        this.lastProductId = parts[parts.length - 1];
      }
      this.saveState();
    }

    onPageHide() {
      /* Catches every other way of leaving the page so the saved scroll
         depth is always current. */
      this.saveState();
    }

    saveState() {
      if (!this.loadedPages.length && !this.lastProductId) return;
      writeState({
        pages: this.loadedPages,
        nextUrl: this.nextUrl,
        scrollY: window.scrollY,
        productId: this.lastProductId,
        savedAt: Date.now(),
      });
    }
  }

  if (!customElements.get('plp-infinite-scroll')) {
    customElements.define('plp-infinite-scroll', PlpInfiniteScroll);
  }
})();
